import { useCallback, useState } from 'react';
import { useFormContext } from '@common-fe/common-fe';
import { toNumber } from 'lodash';

import ROUTES from '@/common/routes';
import useHistory from '@/modules/core/hooks/useHistory';

import {
  PayoutDefinitionField,
  PayoutDefinitionFormValues,
  PayoutOrderCategory,
} from '../../../PayoutDefinitionSetup.types';
import { DEFAULT_COV_TABLE_KEY } from '../../BasicProperties/queries';
import { usePayoutOrderValidation } from '../../PayoutOrder/hooks/usePayoutOrderValidation';
import { usePayoutOrderModalsStore } from '../../PayoutOrder/stores/usePayoutOrderModals.store';
import { UpdatePayoutDefinitionPayload } from '../queries';

const mapFormToPayload = (formValues: PayoutDefinitionFormValues): UpdatePayoutDefinitionPayload => {
  const covTableId = formValues.covTableId;

  return {
    name: formValues.payoutDefinitionName.trim(),
    is_limited_use: formValues.isLimited,
    mp_table_id: formValues.mpTableId ? toNumber(formValues.mpTableId) : null,
    cov_table_id: covTableId && covTableId !== DEFAULT_COV_TABLE_KEY ? toNumber(covTableId) : null,
    allowed_services: formValues.categories.flatMap((category) =>
      category.services.map((service) => ({
        service_id: toNumber(service.id),
        plans: service.plans.map((plan) => ({
          plan_template_id: toNumber(plan.id),
          order: plan.order,
        })),
      }))
    ),
  };
};

interface UseSavePayoutDefinitionParams {
  mutateAsync: (payload: UpdatePayoutDefinitionPayload) => Promise<unknown>;
  isLoading: boolean;
}

const useSavePayoutDefinition = ({ mutateAsync, isLoading }: UseSavePayoutDefinitionParams) => {
  const history = useHistory();
  const { handleSubmit, watch } = useFormContext<PayoutDefinitionFormValues>();
  const { validate } = usePayoutOrderValidation();
  const setValidationTriggered = usePayoutOrderModalsStore((state) => state.setValidationTriggered);
  const setIsSaved = usePayoutOrderModalsStore((state) => state.setIsSaved);

  const [isSuccessModalVisible, setSuccessModalVisible] = useState(false);
  const [isServerError, setServerError] = useState(false);

  const handleSave = useCallback(async () => {
    setServerError(false);
    setValidationTriggered(true);

    const categories: PayoutOrderCategory[] = watch(PayoutDefinitionField.categories) || [];

    await handleSubmit(
      async (formValues) => {
        const { isValid: isPayoutOrderValid } = validate(categories);

        if (!isPayoutOrderValid) {
          return;
        }

        try {
          const payload = mapFormToPayload(formValues);
          await mutateAsync(payload);
          setIsSaved(true);
          setSuccessModalVisible(true);
        } catch {
          setServerError(true);
        }
      },
      () => {
        validate(categories);
      }
    )();
  }, [watch, validate, setValidationTriggered, setIsSaved, handleSubmit, mutateAsync]);

  const handleCloseSuccessModal = useCallback(() => {
    setSuccessModalVisible(false);
    history.push(ROUTES.PAYOUT_DEFINITIONS);
  }, [history]);

  const handleCloseErrorModal = useCallback(() => {
    setServerError(false);
  }, []);

  return {
    handleSave,
    isLoading,
    isSuccessModalVisible,
    isServerError,
    handleCloseSuccessModal,
    handleCloseErrorModal,
  };
};

export default useSavePayoutDefinition;
